import React, { FC, useCallback, useEffect, useState } from 'react'
import VitalCard from './VitalCard'
import { ReactComponent as LungIcon } from '../../../../assets/icons/Lung.svg'
import { ReactComponent as HeartIcon } from '../../../../assets/icons/HeartBeat.svg'
// import { ReactComponent as SkinIcon } from '../../../../assets/icons/BrightSkin.svg'
import { ReactComponent as BrainIcon } from '../../../../assets/icons/MentalDisorder.svg'
import { ReactComponent as MoonIcon } from '../../../../assets/icons/Moon.svg'
import { Card, CardContent, Grid, Typography, useTheme } from '@mui/material'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import { getRingVitals } from '../../../../services/DeviceServices'
import { selectPatientData } from '../../OpdPatientSlice'
import { useAppSelector } from '../../../../app/hooks'
import { selectSafeRanges } from './DeviceSlice'
import { getIconColor, safeRangeKeys } from './Constant'
import dayjs from 'dayjs'
import { ReactComponent as BloodPressureIcon } from '../../../../assets/icons/BloodPressure.svg'
import { ReactComponent as BloodGlucoseIcon } from '../../../../assets/icons/BloodGlucose.svg'

interface StatsCardProps {
  title: string
  value: string
  unit: string
  duration?: string
  type: 'distance' | 'steps' | 'calories'
  icon: FC
  timeStamp: string
}

const styles = (theme: any) => ({
  card: {
    // minWidth: 250,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    borderRadius: 12,
    color: '#fff',
  },
  distance: {
    backgroundColor: '#1E4D4D',
  },
  steps: {
    backgroundColor: '#4D7D1E',
  },
  calories: {
    backgroundColor: '#3D9EA5',
  },
  icon: {
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
})

function SmartRing({
  setOpenDrawer,
  setSelectedVital,
}: {
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedVital: React.Dispatch<React.SetStateAction<string>>
}) {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const patient = useAppSelector(selectPatientData)
  const safeRanges = useAppSelector(selectSafeRanges)
  const [vitals, setVitals] = useState<any>({})

  const extractHours = (durationStr: string): number => {
    const match = durationStr?.match(/(\d+)\s*hr/)
    return match ? parseInt(match?.[1], 10) : 0
  }

  const activityData = [
    {
      title: 'Distance',
      value: `${vitals?.latestActivity?.distance || '-'}`,
      unit: 'Km',
      // duration: '30 min.',
      type: 'distance',
      icon: LocationOnIcon,
      timeStamp: `${
        vitals?.latestActivity?.date
          ? dayjs(vitals?.latestActivity?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      title: 'Steps',
      value: `${vitals?.latestActivity?.stepCount || '-'}`,
      unit: 'Steps',
      // duration: '30 min.',
      type: 'steps',
      icon: DirectionsWalkIcon,
      timeStamp: `${
        vitals?.latestActivity?.date
          ? dayjs(vitals?.latestActivity?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      title: 'Calories',
      value: `${vitals?.latestActivity?.calories || '-'}`,
      unit: 'Calories',
      // duration: '30 min.',
      type: 'calories',
      icon: LocalFireDepartmentIcon,
      timeStamp: `${
        vitals?.latestActivity?.date
          ? dayjs(vitals?.latestActivity?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
  ]

  const cardData = [
    {
      name: 'Heart Rate',
      subName: 'Current',
      value: `${vitals?.latestHeartRate?.heartRate || '-'}`,
      unit: 'BPM',
      note: `Normal Range is ${safeRanges?.[safeRangeKeys?.HEARTRATE]?.min}-${
        safeRanges?.[safeRangeKeys?.HEARTRATE]?.max
      } BPM`,
      icon: (
        <HeartIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.HEARTRATE,
            parseInt(vitals?.latestHeartRate?.heartRate)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.latestHeartRate?.date
          ? dayjs(vitals?.latestHeartRate?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      name: 'Spo2',
      subName: 'Current',
      value: `${vitals?.latestSpo2?.bloodOxygen || '-'}`,
      unit: '%',
      note: `Normal Range is ${safeRanges?.[safeRangeKeys?.SPO2]?.min}-${
        safeRanges?.[safeRangeKeys?.SPO2]?.max
      } %`,
      icon: (
        <LungIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.SPO2,
            parseInt(vitals?.latestSpo2?.bloodOxygen)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.latestSpo2?.date
          ? dayjs(vitals?.latestSpo2?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      name: 'Heart Rate Var',
      subName: 'Current',
      value: `${vitals?.hrv?.hrv || '-'}`,
      unit: 'ms',
      note: `Normal Range is ${
        safeRanges?.[safeRangeKeys?.HEARTRATEVAR]?.min
      }-${safeRanges?.[safeRangeKeys?.HEARTRATEVAR]?.max} ms`,
      icon: (
        <HeartIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.HEARTRATEVAR,
            parseInt(vitals?.hrv?.hrv)
          )}
        />
      ),
      background: '#fa7900',
      details: true,
      timeStamp: `${
        vitals?.hrv?.date
          ? dayjs(vitals?.hrv?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      name: 'Stress',
      subName: 'Current',
      value: `${vitals?.stress?.stress || '-'}`,
      unit: '',
      note: `Normal Range is ${safeRanges?.[safeRangeKeys?.STRESS]?.min}-${
        safeRanges?.[safeRangeKeys?.STRESS]?.max
      }`,
      icon: (
        <BrainIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.STRESS,
            parseInt(vitals?.stress?.stress)
          )}
        />
      ),
      background: '#E91E63',
      details: true,
      timeStamp: `${
        vitals?.stress?.date
          ? dayjs(vitals?.stress?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    // {
    //   name: 'Skin Temperature',
    //   subName: 'Current',
    //   value: `${vitals?.latestTemperature?.temperature || '-'}`,
    //   unit: '°F',
    //   note: `Normal Range is ${
    //     safeRanges?.[safeRangeKeys?.SKINTEMPERATURE]?.min
    //   }-${safeRanges?.[safeRangeKeys?.SKINTEMPERATURE]?.max} °F`,
    //   icon: (
    //     <SkinIcon
    //       height={40}
    //       width={40}
    //       color={getIconColor(
    //         safeRanges,
    //         safeRangeKeys?.SKINTEMPERATURE,
    //         parseInt(vitals?.latestTemperature?.temperature)
    //       )}
    //     />
    //   ),
    //   background: '#55CEBC',
    //   details: true,
    //   timeStamp: `${
    //     vitals?.latestTemperature?.date
    //       ? dayjs(vitals?.latestTemperature?.date).format('MMMM D, YYYY h:mm A')
    //       : '-'
    //   }`,
    // },
    {
      name: 'Sleep',
      subName: 'Current',
      value: `${vitals?.totalSleepHours?.totalSleepHours || '-'}`,
      unit: '',
      // note: `Normal Range is ${safeRanges?.[safeRangeKeys?.SLEEP]?.min}-${
      //   safeRanges?.[safeRangeKeys?.SLEEP]?.max
      // } h`,
      note: 'Normal Range is 8 h',
      icon: (
        <MoonIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.SLEEP,
            extractHours(vitals?.totalSleepHours?.totalSleepHours)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.totalSleepHours?.mainStartDateTime
          ? dayjs(vitals?.totalSleepHours?.mainStartDateTime).format(
              'MMMM D, YYYY h:mm A'
            )
          : '-'
      } - ${
        vitals?.totalSleepHours?.mainEndDateTime
          ? dayjs(vitals?.totalSleepHours?.mainEndDateTime).format(
              'MMMM D, YYYY h:mm A'
            )
          : '-'
      }`,
    },
    {
      name: 'Blood Pressure Systolic',
      subName: 'Hyper Tension',
      value: vitals?.bloodPressure?.systolicBp || '-',
      unit: 'mm Hg',
      note: `Normal Range is ${
        safeRanges?.[safeRangeKeys?.BLOODPRESSURESYSTOLIC]?.min
      }-${safeRanges?.[safeRangeKeys?.BLOODPRESSURESYSTOLIC]?.max} mmHg`,
      icon: (
        <BloodPressureIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.BLOODPRESSURESYSTOLIC,
            parseInt(vitals?.bloodPressure?.systolicBp)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.bloodPressure?.date
          ? dayjs(vitals?.bloodPressure?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
    {
      name: 'Blood Pressure Diastolic',
      subName: 'Hyper Tension',
      value: vitals?.bloodPressure?.diastolicBp || '-',
      unit: 'mm Hg',
      note: `Normal Range is ${
        safeRanges?.[safeRangeKeys?.BLOODPRESSUREDIASTOLIC]?.min
      }-${safeRanges?.[safeRangeKeys?.BLOODPRESSUREDIASTOLIC]?.max} mmHg`,
      icon: (
        <BloodPressureIcon
          height={40}
          width={40}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.BLOODPRESSUREDIASTOLIC,
            parseInt(vitals?.bloodPressure?.diastolicBp)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.bloodPressure?.date
          ? dayjs(vitals?.bloodPressure?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },

    {
      name: 'Blood Glucose',
      subName: 'Current',
      value: vitals?.bloodGlucose?.bloodGlucose || '-',
      unit: 'mg/dL',
      note: `Normal Range is ${
        safeRanges?.[safeRangeKeys?.BLOODGLUCOSE]?.min
      }-${safeRanges?.[safeRangeKeys?.BLOODGLUCOSE]?.max} mg/dL`,
      icon: (
        <BloodGlucoseIcon
          height={55}
          width={55}
          color={getIconColor(
            safeRanges,
            safeRangeKeys?.BLOODGLUCOSE,
            parseInt(vitals?.bloodGlucose?.bloodGlucose)
          )}
        />
      ),
      details: true,
      timeStamp: `${
        vitals?.bloodGlucose?.date
          ? dayjs(vitals?.bloodGlucose?.date).format('MMMM D, YYYY h:mm A')
          : '-'
      }`,
    },
  ]

  const handleCardClick = (selectedVital: string) => {
    setOpenDrawer((prevData: boolean) => !prevData)
    setSelectedVital(selectedVital)
  }

  const StatsCard: React.FC<StatsCardProps> = ({
    title,
    value,
    unit,
    duration,
    type,
    icon: Icon,
    timeStamp,
  }) => {
    return (
      <Card className={`${classes?.card} ${classes?.[type]}`}>
        <CardContent>
          <Typography
            variant='subtitle1'
            display={'flex'}
            alignItems={'center'}
          >
            {title} <Icon />
          </Typography>
          <Typography variant='h5' sx={{ whiteSpace: 'nowrap' }}>
            <strong>{value}</strong> {unit}
          </Typography>
          {duration && (
            <Typography variant='body2'>Duration is {duration}.</Typography>
          )}

          <Typography variant='body2'>{timeStamp}</Typography>
        </CardContent>
      </Card>
    )
  }

  const handleGetVitals = useCallback(async () => {
    const response = await getRingVitals(patient?.muhId)
    if (response?.success) {
      setVitals(response?.data)
    }
  }, [patient?.muhId])

  useEffect(() => {
    handleGetVitals()
  }, [handleGetVitals])

  return (
    <div>
      <Grid container direction={'row'} rowGap={3} spacing={1.5} mb={3}>
        {cardData?.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2} key={index}>
            <VitalCard
              name={item?.name}
              subName={item?.subName}
              value={item?.value}
              unit={item?.unit}
              note={item?.note}
              icon={item?.icon}
              details={item?.details}
              onClick={() => handleCardClick(item?.name)}
              timeStamp={item?.timeStamp}
            />
          </Grid>
        ))}
      </Grid>
      <Grid container direction={'row'} rowGap={3} spacing={1.5}>
        {activityData?.map((item: any, index) => {
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={index}>
              <StatsCard
                title={item?.title}
                value={item?.value}
                unit={item?.unit}
                type={item?.type}
                icon={item?.icon}
                timeStamp={item?.timeStamp}
              />
            </Grid>
          )
        })}
      </Grid>
    </div>
  )
}

export default SmartRing
